import { IExtendedTelemetryItem, IEventProperty } from '@ms/1ds-core-js';
import { DataField, DataFieldType, DataClassification, logError, Category } from '@ms/oteljs';

// Constants
const DATA_TOKEN = 'Data.';
const CONTRACT_TOKEN = 'zC.';

/**
 * Aria token for Office.Telemetry
 */
export const ARIA_OFFICE_TELEMETRY_TOKEN = 'f998cc5ba4d448d6a1e8e913ff18be94-dd122e0a-fcf8-4dc5-9dbb-6afac5325183-7405'; // Office.Telemetry
export const CUSTOMER_CONTENT_TENANT_TOKEN = 'b22a201c3f1d41d28ccc399ba6cc9ca2-1972c77f-1f79-4283-a0f9-b4ddc4646f55-7121';

/**
 * Enum for property types.
 *
 * Duplicated here to keep references to 1ds-core-js from bloating our code.
 * Referred from @ms/1ds-core-js in shared/core/src/Enums.ts
 * Note that the '1DS SDK tests'=>'EventPropertyType' verify that these are in sync
 */
export const enum EventPropertyType {
  Unspecified = 0,
  String = 1,
  Int32 = 2,
  UInt32 = 3,
  Int64 = 4,
  UInt64 = 5,
  Double = 6,
  Bool = 7,
  Guid = 8,
  DateTime = 9
}

interface IOneDSFieldOptions {
  prependDataToken: boolean;
  customerContentEnabled: boolean;
}

/**
 * Add OTelJS DataField fields to a 1DS data object
 * @param data The object to hold 1DS fields
 * @param fields The DataField fields to add
 * @param prependDataToken Boolean, whether or not to prepend "Data."
 */
export function addOneDSFields(data: IExtendedTelemetryItem['data'], fields: DataField[] | undefined, options: IOneDSFieldOptions): void {
  if (fields) {
    fields.forEach(field => {
      // We can usually only send SystemMetadata or EssentialServiceMetadata
      // CustomerContent is also possible if it's enabled
      // We drop other fields
      if (
        field.classification &&
        !(
          field.classification === DataClassification.SystemMetadata ||
          field.classification === DataClassification.EssentialServiceMetadata ||
          (options.customerContentEnabled && field.classification === DataClassification.CustomerContent)
        )
      ) {
        return;
      }

      let modifiedFieldName;
      if (options.prependDataToken) {
        if (field.name.substr(0, 3) === CONTRACT_TOKEN) {
          modifiedFieldName = CONTRACT_TOKEN + DATA_TOKEN + field.name.substr(3);
        } else {
          modifiedFieldName = DATA_TOKEN + field.name;
        }
      } else {
        modifiedFieldName = field.name;
      }

      let eventPropertyType: number;
      switch (field.dataType) {
        case DataFieldType.Double:
          eventPropertyType = EventPropertyType.Double;
          break;
        case DataFieldType.Int64:
          eventPropertyType = EventPropertyType.Int64;
          break;
        case DataFieldType.Guid:
          eventPropertyType = EventPropertyType.Guid;
          break;
        case DataFieldType.String:
          // Strings and booleans are set directly, not as IEventProperties -
          // tables get created with the appropriate data types on the collector side.
          // This helps to save about 25% of the event size!
          // The 1DS SDK assigns a default type of Double to any numbers, so we still
          // to send the data types for those values.

          // Limit the maximum allowed string length on a datafield to avoid accidentally going over
          // the maximum allowed serialized size for a 1DS event and having the event dropped.
          // That and 1ds-post v2.4.4 and v3.0.0 have a bug that will put the code into an infinite
          // loop if that condition is trigged.
          data![modifiedFieldName] = typeof field.value === 'string' ? field.value.substr(0, 25000) : field.value;
          return;
        case DataFieldType.Boolean:
        default:
          data![modifiedFieldName] = field.value;
          return;
      }

      let eventProperty: IEventProperty = {
        value: field.value,
        propertyType: eventPropertyType
      };

      data![modifiedFieldName] = eventProperty;
    });
  }
}

export function tryCatch<T>(func: () => T, errorReturn: T) {
  try {
    return func();
  } catch (error) {
    logError(Category.Sink, '1DS Sink', error);
    return errorReturn;
  }
}

/**
 * Converts a date object into an ISO string. This is needed because not all browsers support ISO string format
 * on the date.
 * Copied from Utils.ts in @ms/1ds-core-js to avoid having to import the whole thing when only using the OneDSHelper.
 * @param date - The date which needs to be converted to ISO format.
 * @returns The date in ISO format.
 */
export function getISOString(date: Date): string {
  return (
    date.getUTCFullYear() +
    '-' +
    twoDigit(date.getUTCMonth() + 1) +
    '-' +
    twoDigit(date.getUTCDate()) +
    'T' +
    twoDigit(date.getUTCHours()) +
    ':' +
    twoDigit(date.getUTCMinutes()) +
    ':' +
    twoDigit(date.getUTCSeconds()) +
    '.' +
    threeDigit(date.getUTCMilliseconds()) +
    'Z'
  );
}

function twoDigit(n: number): string {
  // Format integers to have at least two digits.
  return n < 10 ? '0' + n : n.toString();
}

function threeDigit(n: number): string {
  // Format integers to have at least three digits.
  if (n < 10) {
    return '00' + n;
  }
  if (n < 100) {
    return '0' + n;
  }
  return n.toString();
}
